import React, { useCallback, useEffect, useState, createContext } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import localization from 'moment/locale/pt-br'
import { useParams } from 'react-router'
import useRouter from 'utils/useRouter'
import { useCasoService } from 'hooks/useCasoService'

// UI Components & Layout
import { Page, Header, ObjetoAvatar, SecaoGenerica } from 'components'
import { Card, CardContent, Grid, Button, Typography, Avatar, Fab, Divider } from '@material-ui/core'

// Icons
import AddIcon from '@material-ui/icons/Add'
import FolderIcon from '@material-ui/icons/Folder'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import BusinessIcon from '@material-ui/icons/Business'
import AssignmentIcon from '@material-ui/icons/Assignment'

// Global and Local Business Logic Components
import { HistoricoOperacoes } from 'components/HistoricoOperacoes'
import { ListaInformacoes } from 'views/Informacao/ListaInformacoes'
import { ViewModeToggle } from './components/UI/ViewModeToggle'
import { CustomDialog } from './components/UI/CustomDialog'
import { Carregando } from './components/UI/Carregando'
import { MenuAcoes } from './components/Menus/MenuAcoes'
import { MenuAdd } from './components/Menus/MenuAdd'
import { ModalImportacao } from './components/ModalImportacao'
import { ObjetosCasoContainer, ObjetosCaso } from './components/ObjetosCaso'


// Estilos Isolados
import { useStyles } from './Caso.styles'

import { DetalhesCasoContext } from './DetalhesCasoContext'

export const Caso = (props) => {
  const { route } = props
  moment.locale('pt-br', localization)
  const classes = useStyles()
  const router = useRouter()
  const params = useParams()

  const casoService = useCasoService()
  const [processandoCaso, setProcessandoCaso] = useState(true)
  const [context, setContext] = useState(null)
  const dados = context?.caso
  // Estados do Dashboard (Antigo Results)
  const [anchorEl, setAnchorEl] = useState(null)
  const [anchorElAcoes, setAnchorElAcoes] = useState(null)
  const [openEspelhoImportarObjetos, setOpenEspelhoImportarObjetos] = useState(false)
  const [mensagensAlerta, setMensagensAlerta] = useState([])
  const [openAlert, setOpenAlert] = useState(false)
  const [viewMode, setViewMode] = useState('grid')

  // Autenticação e Fetching Inicial
  useEffect(() => {
    if (!params.id) {
      router.history.push('/auth/login')
    }
  }, [params, router.history])

  const fetchCaso = useCallback(async () => {
    const result = await casoService.listarCasoComContagem(params.id, setProcessandoCaso)
    setContext(result)
  }, [params.id])

  useEffect(() => {
    fetchCaso()
  }, [fetchCaso])

  // Handlers de Interação
  const handleClickBotaoAcoes = (event) => setAnchorElAcoes(event.currentTarget)
  const handleCloseAcoes = () => setAnchorElAcoes(null)

  const handleClickBotaoAdd = (event) => setAnchorEl(event.currentTarget)
  const handleCloseBotaoAdd = () => setAnchorEl(null)

  const handleCloseAlert = () => {
    setMensagensAlerta([])
    setOpenAlert(false)
  }

  return (
    <Page className={classes.root} title={`${route.secao} - SIGEP`}>
      <Header titulo={`${route.secao}`} overline={route.secao} />
      <DetalhesCasoContext.Provider value={context}>
        <div className={classes.resultsWrapper}>
          {!dados ? (
            <Carregando />
          ) : (
            <>
              {/* Cabeçalho Premium do Caso (Dashboard Rebranding) */}
              <Card className={classes.headerCard}>
                <CardContent style={{ padding: '24px' }}>
                  <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                    <Grid item xs={12} sm={8} md={9} style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <ObjetoAvatar tipo="CASO" />
                      <Typography variant="h3" className={classes.caseTitle}>
                        {dados.identificador.toUpperCase()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3} style={{ textAlign: 'right' }}>
                      <Button aria-controls="customized-menu" aria-haspopup="true" variant="contained" color="primary" onClick={handleClickBotaoAcoes} endIcon={<ArrowDropDownIcon />} style={{ borderRadius: '8px', padding: '10px 24px', fontWeight: 'bold' }}>
                        Ações
                      </Button>
                      <MenuAcoes id="customized-menu" anchorEl={anchorElAcoes} keepMounted open={Boolean(anchorElAcoes)} onClose={handleCloseAcoes} setOpenEspelhoImportarObjetos={setOpenEspelhoImportarObjetos} />
                    </Grid>
                  </Grid>

                  <Divider style={{ margin: '20px 0 24px 0' }} />

                  {/* Linha Descritiva de Alta Densidade: Ocupação 100% Horizontal sem estrangulamento */}
                  <Typography variant="subtitle1" className={classes.caseSubtitle} style={{ marginTop: 0, marginBottom: '24px' }}>
                    {dados.descricao || 'Sem descrição adicionada'}
                  </Typography>

                  <Grid container spacing={3} className={classes.metaGrid} alignItems="stretch">
                    <Grid item xs={12} sm={6} md={4}>
                      <div className={classes.metaItem}>
                        <CalendarTodayIcon className={classes.metaIcon} />
                        <div className={classes.metaTextContainer}>
                          <Typography className={classes.metaLabel}>DATA DO CASO</Typography>
                          <Typography className={classes.metaValue}>{moment(dados.data).format('DD/MM/YYYY HH:mm')}</Typography>
                        </div>
                      </div>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <div className={classes.metaItem}>
                        <BusinessIcon className={classes.metaIcon} />
                        <div className={classes.metaTextContainer}>
                          <Typography className={classes.metaLabel}>SETOR RESPONSÁVEL</Typography>
                          <Typography className={classes.metaValue}>{dados.setRes ? dados.setRes.nome?.toUpperCase() : 'NÃO INFORMADO'}</Typography>
                        </div>
                      </div>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <div className={classes.metaItem}>
                        <AssignmentIcon className={classes.metaIcon} />
                        <div className={classes.metaTextContainer}>
                          <Typography className={classes.metaLabel}>PROCESSO SEI</Typography>
                          <Typography className={classes.metaValue}>{dados.processoSei ? dados.processoSei.toUpperCase() : 'NÃO INFORMADO'}</Typography>
                        </div>
                      </div>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <SecaoGenerica titulo="OBJETOS RELACIONADOS" rightAction={<ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />}>
                <ObjetosCasoContainer textoVazio="Nenhum objeto associado ao caso" viewMode={viewMode}>
                  <ObjetosCaso tipo="requisicao" viewMode={viewMode} />
                  <ObjetosCaso tipo="vestigio" viewMode={viewMode} />
                  <ObjetosCaso tipo="cadaver" viewMode={viewMode} />
                  <ObjetosCaso tipo="periciando" viewMode={viewMode} />
                  <ObjetosCaso tipo="local" viewMode={viewMode} />
                  <ObjetosCaso tipo="veiculo" viewMode={viewMode} />
                </ObjetosCasoContainer>
              </SecaoGenerica>

              <SecaoGenerica titulo="SOLICITAÇÕES DE EXAME">
                <ObjetosCasoContainer textoVazio="Nenhuma solicitação associada ao caso">
                  <ObjetosCaso md={12} sm={12} xs={12} tipo="solicitacao" />
                </ObjetosCasoContainer>
              </SecaoGenerica>

              <SecaoGenerica titulo="ARQUIVOS RELACIONADOS">
                <ObjetosCasoContainer textoVazio="Nenhum arquivo relacionado">
                  <ObjetosCaso tipo="arquivo" />
                </ObjetosCasoContainer>
              </SecaoGenerica>

              <SecaoGenerica titulo="INFORMAÇÕES ADICIONADAS">
                <Grid item md={12} sm={12} xs={12}>
                  <ListaInformacoes tipo="caso" id={dados.id} />
                </Grid>
              </SecaoGenerica>

              <SecaoGenerica titulo="HISTÓRICO DE OPERAÇÕES">
                <Grid item md={12} sm={12} xs={12} style={{ paddingTop: '10px' }}>
                  <HistoricoOperacoes id={dados.id} tipo="CASO" />
                </Grid>
              </SecaoGenerica>

              <Fab color="primary" aria-label="add" style={{ position: 'fixed', bottom: '30px', right: '50px', zIndex: 1000 }} onClick={handleClickBotaoAdd}>
                <AddIcon />
              </Fab>
              <MenuAdd id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleCloseBotaoAdd} />

              {openEspelhoImportarObjetos && (
                <ModalImportacao aberto={openEspelhoImportarObjetos} setAberto={setOpenEspelhoImportarObjetos} setMensagensAlerta={setMensagensAlerta} setOpenAlert={setOpenAlert} titulo="IMPORTAR OBJETOS" fetchCaso={fetchCaso} />
              )}
            </>
          )}
        </div>
      </DetalhesCasoContext.Provider>
      <CustomDialog mensagens={mensagensAlerta} titulo="Não é possível realizar a operação por não atender aos seguintes requisitos" open={openAlert} onClose={handleCloseAlert} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" />
    </Page>
  )
}
